import { useContentResource } from "@ctv/core";
import { Card, countries, Text, Token, View } from "@ctv/shared-core/src";
import { Fragment } from "react";
import { useFormContext } from "react-hook-form";
import { StyleSheet } from "react-native";
import {
  ExistingProductTypes,
  INDUSTRIES,
  JOB_TITLES,
  TRAVEL_SPENDINGS,
} from "../../../_constants";
import { RegistrationFormValue } from "../../../types";
import ProductType from "../../RegistrationForm/_components/ProductType";
import ReviewRow from "./ReviewRow";
import ReviewStack from "./ReviewStack";

import CreditCardIcon from "@traveloka/icon-kit-web/react/IcPaymentCreditCard";
import InvoiceIcon from "@traveloka/icon-kit-web/react/IcPolicy";

export default function ReviewPage1() {
  const {
    CorporateRegistration: cr,
    CorporateRegistrationDropdown: crd,
    CorporateRegistrationProductType: crpt,
  } = useContentResource();

  const { getValues } = useFormContext<RegistrationFormValue>();
  const {
    picName,
    phone,
    picEmail,
    picJobTitle,
    country,
    website,
    officePhone,
    officeAddress,
    companyName,
    companyIndustry,
    monthlyTravelSpending,
    productNeeded,
    productRequest,
    paymentMethod,
  } = getValues();

  const jobTitleList = JOB_TITLES.map((job) => ({
    label: crd[job],
    value: job,
  }));

  const industryList = INDUSTRIES.map((industry) => ({
    label: crd[industry],
    value: industry,
  }));

  const travelSpendingList = TRAVEL_SPENDINGS.map((spending) => ({
    label: crd[spending],
    value: spending,
  }));

  const countryList = countries
    .filter((c) => c.countryId === "ID")
    .map((c) => ({ value: c.countryId, label: c.countryName }));

  const PAYMENT_METHOD_CONTENT = {
    INVOICE: {
      Icon: InvoiceIcon,
      name: cr.invoice,
      terms: cr.invoiceHelpV2,
    },
    CREDIT_CARD: {
      Icon: CreditCardIcon,
      name: cr.creditCard,
      terms: cr.creditCardHelp,
    },
  };

  const paymentInfo = PAYMENT_METHOD_CONTENT[paymentMethod];
  const PaymentInfoIcon = paymentInfo.Icon;

  return (
    <Fragment>
      {/* Section General Information */}
      <Text variant="title-1" style={styles.section}>
        {cr.reviewAboutSectionV2}
      </Text>
      <ReviewStack>
        <ReviewRow label={cr.picNameLabelV2} value={picName} />
        <ReviewRow
          label={cr.picPhoneLabel}
          value={phone.prefix + phone.phone}
        />
        <ReviewRow label={cr.emailLabelV2} value={picEmail} />
        <ReviewRow
          label={cr.jobTitleLabel}
          value={jobTitleList.find((a) => a.value === picJobTitle)?.label}
        />
      </ReviewStack>
      {/* Section Company Information */}
      <Text variant="title-1" style={styles.section}>
        {cr.reviewCompanySectionV2}
      </Text>
      <ReviewStack>
        <ReviewRow label={cr.companyNameLabel} value={companyName} />
        <ReviewRow
          label={cr.companyIndustryLabelV2}
          value={industryList.find((a) => a.value === companyIndustry)?.label}
        />
        <ReviewRow
          label={cr.countryLabel}
          value={countryList.find((a) => a.value === country)?.label}
        />
        <ReviewRow label={cr.officeAddressLabel} value={officeAddress} />
        <ReviewRow
          label={cr.officePhoneLabel}
          value={officePhone.prefix + officePhone.phone}
        />
        <ReviewRow label={cr.websiteLabelV2} value={website} />
        <ReviewRow
          label={cr.expensesLabelV2}
          value={
            travelSpendingList.find((a) => a.value === monthlyTravelSpending)
              ?.label
          }
        />
        {/* Section Products */}
      </ReviewStack>
      <Text variant="title-1" style={styles.section}>
        {cr.reviewProductSectionV2}
      </Text>
      <Card style={styles.productList}>
        <View row spacing="m">
          {(Object.keys(ExistingProductTypes) as ExistingProductTypes[])
            .filter((productType) => productNeeded.includes(productType))
            .map((p) => (
              <ProductType key={p} type={p} isSelected />
            ))}
        </View>
      </Card>
      <Text variant="title-1" style={styles.section}>
        {cr.requestProductSection}
      </Text>
      <ReviewStack>
        <ReviewRow
          label={cr.reviewOtherProductLabel}
          value={productRequest.map((product) => crpt[product]).join(", ")}
        />
      </ReviewStack>
      {/* Section Payment Method */}
      <Text variant="title-1" style={styles.section}>
        {cr.reviewPaymentMethodSection}
      </Text>
      <Card style={styles.paymentMethod}>
        <View style={styles.paymentMethodIconContainer}>
          <PaymentInfoIcon height={24} width={24} />
          <Text style={styles.paymentMethodText} variant="title-2">
            {paymentInfo.name}
          </Text>
        </View>
        <Text variant="ui-tiny">{paymentInfo.terms}</Text>
      </Card>
    </Fragment>
  );
}

const styles = StyleSheet.create({
  back: {
    alignSelf: "flex-start",
  },
  productList: {
    padding: Token.spacing.l,
  },
  nextButton: {
    alignSelf: "flex-end",
    marginTop: Token.spacing.xxl,
  },
  headline: {
    marginTop: Token.spacing.xl,
  },
  section: {
    marginTop: Token.spacing.xl,
    marginBottom: Token.spacing.m,
  },
  paymentMethod: {
    padding: Token.spacing.l,
  },
  paymentMethodIconContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: Token.spacing.m,
  },
  paymentMethodText: {
    marginLeft: Token.spacing.s,
  },
});
